import {
  SOCCER_KICK_RANGE,
  TENNIS_POINTS_TO_WIN,
  TENNIS_WIN_BY,
  isSportsPrivate,
  isSportsState,
  type SportsAction,
  type SportsPrivate,
  type SportsState,
  type SportsZone,
  type TennisAim,
  type TennisCourtWire,
} from "@townsy/contracts";

/**
 * Townsy Sports Center overlay (ADR-0016), as plain data: which controls apply where the visitor stands (the server
 * says so in `venue.private`), whether each is usable right now (and why not), and the score lines. The components
 * (SportsHud.tsx, SportsCompact.tsx) only render this; the keyboard shortcuts use the same buttons.
 *
 * Keys: F = the main action (hold to charge Shoot / Kick, release to let go; Play; Swing / Serve), G = the second
 * (Pass / light touch / Play vs AI), R = the third (Steal / Leave court); A / D nudge the soccer aim WHILE F is held
 * (walking pauses while you charge). Otherwise WASD, the arrows, E, Enter and Escape stay the town's own keys.
 */
export type SportsKey = "F" | "G" | "R";
export const SPORTS_KEY_CODES: Record<string, SportsKey> = { KeyF: "F", KeyG: "G", KeyR: "R" };
export type ChargeKindView = "shoot" | "kick";

export interface SportsHudButton {
  id: string;
  label: string;
  key: SportsKey;
  /** The intent sent on press (for a charged button: sent on release with the power, and the aim for a kick). */
  action: SportsAction;
  /** Hold to charge, release to send (Shoot, Kick). */
  charge?: ChargeKindView;
  /** A swing: the overlay adds the aim zone (from the way the visitor faces). */
  aimed?: boolean;
  /** null when usable; otherwise the reason shown on the button's title and below it. */
  disabledReason: string | null;
  primary: boolean;
}

export interface SportsHudView {
  zone: SportsZone | null;
  title: string;
  /** Score / status lines (first is the most important). */
  lines: string[];
  buttons: SportsHudButton[];
  hint: string;
  /** The power meter to show (and, for basketball, its sweet band). */
  meter: ChargeKindView | null;
  /** Soccer: the aim ◀ ▶ buttons. */
  aimButtons: boolean;
  /** Tennis: the side you play (0 south, 1 north), for "aim left / right" from your point of view. */
  mySide: number | null;
  /** Tennis: the short how-to line (shown until dismissed). */
  help: string | null;
}

const button = (id: string, label: string, key: SportsKey, action: SportsAction, disabledReason: string | null, extra: Partial<SportsHudButton> = {}): SportsHudButton => ({
  id,
  label,
  key,
  action,
  disabledReason,
  primary: false,
  ...extra,
});

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const SPORTS_GUIDE =
  "Walk onto a court to play: basketball in the middle, the soccer pitch on the left (west), two tennis courts on the right (east). Free to play; scores last for this visit.";
export const TENNIS_HELP =
  "How to play: serve with F. Run to the ball (hold Shift, or Run on a touch screen) and swing (F) when the ring at your feet turns green. Hold A or D as you swing to aim left or right.";

const base = { meter: null, aimButtons: false, mySide: null, help: null } as const;

export function sportsHudView(sharedRaw: unknown, mineRaw: unknown, localActorId: string | null): SportsHudView {
  const shared: SportsState | null = isSportsState(sharedRaw) ? sharedRaw : null;
  const mine: SportsPrivate | null = isSportsPrivate(mineRaw) ? mineRaw : null;
  const zone = mine?.zone ?? null;
  const playing = mine?.playing ?? null;

  if (playing && shared) {
    const court = shared.tennis[playing.court];
    if (court) return tennisPlayingView(court, playing.court, playing.side, mine!);
  }
  if (zone === "basketball") return basketballView(shared, mine!, localActorId);
  if (zone === "soccer") return soccerView(shared, mine!);
  if (zone === "tennis" && mine && mine.court !== null && mine.side !== null) return tennisJoinView(shared?.tennis[mine.court] ?? null, mine.court, mine.side);
  return { ...base, zone: null, title: "Pick a court", lines: mine ? myTotals(mine) : [], buttons: [], hint: SPORTS_GUIDE };
}

function myTotals(mine: SportsPrivate): string[] {
  const parts: string[] = [];
  if (mine.points) parts.push(`${plural(mine.points, "basketball point")}`);
  if (mine.goals || mine.saved) parts.push(`${plural(mine.goals, "goal")}, ${mine.saved} saved by the goalie`);
  if (mine.tennisWins) parts.push(`${plural(mine.tennisWins, "tennis match", "tennis matches")} won`);
  return parts.length ? [`This visit: ${parts.join(" · ")}`] : [];
}

function basketballView(shared: SportsState | null, mine: SportsPrivate, localActorId: string | null): SportsHudView {
  const bb = shared?.basketball ?? null;
  const holding = Boolean(bb && localActorId && bb.holder === localActorId);
  const someoneElse = Boolean(bb && bb.holder && bb.holder !== localActorId);
  const needBall = "Walk to the ball to pick it up.";
  let ballLine = "The ball is loose: walk to it to pick it up.";
  if (holding) ballLine = "You have the ball: face a hoop, hold Shoot, let go in the green.";
  else if (someoneElse) ballLine = `${bb!.holderName ?? "Someone"} has the ball.`;
  else if (bb?.phase === "shot") ballLine = "Shot in the air…";
  else if (bb?.phase === "pass") ballLine = "Pass in the air…";
  else if (bb?.phase === "scored") ballLine = "Basket! The ball goes back to the centre circle.";
  const top = bb?.top.length ? `Top scorers: ${bb.top.map((t) => `${t.name} ${t.points}`).join(" · ")}` : null;
  return {
    ...base,
    zone: "basketball",
    title: "Basketball court",
    lines: [`Your points: ${mine.points}`, ballLine, ...(top ? [top] : [])],
    buttons: [
      button("shoot", "Shoot", "F", { type: "shoot", power: 0 }, holding ? null : needBall, { primary: true, charge: "shoot" }),
      button("pass", "Pass", "G", { type: "pass" }, holding ? null : needBall),
      button("steal", "Steal", "R", { type: "steal" }, someoneElse ? null : holding ? "You have the ball." : "Nobody has the ball."),
    ],
    meter: "shoot",
    hint: "The green band on the meter is the right power for your distance (farther needs more). 2 points inside the arc, 3 beyond. Pass goes to the player you face (ringed); steal works within 1.5 m.",
  };
}

function soccerView(shared: SportsState | null, mine: SportsPrivate): SportsHudView {
  const sc = shared?.soccer ?? null;
  const ready = !sc || sc.phase === "live";
  let status = "";
  if (sc?.phase === "goal") status = "GOAL!";
  else if (sc?.phase === "saved") status = "Saved by the AI goalie.";
  else if (sc?.phase === "out") status = "Out: the ball goes back to the spot.";
  const wait = ready ? null : "Wait for the ball to come back to the spot.";
  return {
    ...base,
    zone: "soccer",
    title: "Soccer pitch",
    lines: [`Your goals: ${mine.goals} · Saved by the goalie: ${mine.saved}`, ...(status ? [status] : []), ...(sc ? [`Everyone this visit: ${plural(sc.goals, "goal")}, ${plural(sc.saves, "save")}`] : [])],
    buttons: [
      button("kick", "Kick", "F", { type: "kick" }, wait, { primary: true, charge: "kick" }),
      button("tap", "Touch", "G", { type: "tap" }, wait),
    ],
    meter: "kick",
    aimButtons: true,
    hint: `Within ${SOCCER_KICK_RANGE} m of the ball: hold F, nudge the arrow with A / D (or ◀ ▶), let go. Corners beat the goalie more often than the middle; more power is faster but less precise. G is a light touch; walking into the ball dribbles it.`,
  };
}

function scoreLine(court: TennisCourtWire, mySide: number | null): string {
  const [a, b] = court.sides;
  if (mySide === null) return `${a.name} ${a.points} – ${b.points} ${b.name}`;
  const me = court.sides[mySide]!;
  const them = court.sides[mySide === 0 ? 1 : 0]!;
  return `You ${me.points} – ${them.points} ${them.kind === "open" ? "(no opponent yet)" : them.name}`;
}

function tennisJoinView(court: TennisCourtWire | null, courtIndex: number, side: number): SportsHudView {
  const here = court?.sides[side];
  const taken = here?.kind === "human";
  const inRally = court?.phase === "rally";
  const playReason = taken ? `${here!.name} is playing this side.` : here?.kind === "ai" && inRally ? "Wait for the point to finish." : null;
  return {
    ...base,
    zone: "tennis",
    title: `Tennis court ${courtIndex + 1}`,
    lines: court && court.phase !== "open" ? [scoreLine(court, null), ...(court.message ? [court.message] : [])] : [`You are on the ${side === 0 ? "south" : "north"} side. Nobody is playing here.`],
    buttons: [
      button("play", here?.kind === "ai" ? "Take over from the AI" : "Play", "F", { type: "tennis_play" }, playReason, { primary: true }),
      button("play-ai", "Play vs AI", "G", { type: "tennis_play", vsAi: true }, playReason),
    ],
    hint: `Stand on a side of the court and press Play: you get a racket. Alone? The AI joins after a few seconds. First to ${TENNIS_POINTS_TO_WIN}, win by ${TENNIS_WIN_BY}.`,
  };
}

function tennisPlayingView(court: TennisCourtWire, courtIndex: number, side: number, mine: SportsPrivate): SportsHudView {
  let swingLabel = "Swing";
  let swingReason: string | null = null;
  if (court.phase === "serve") {
    if (court.server === side) swingLabel = "Serve";
    else swingReason = `${court.sides[court.server]!.name} serves.`;
  } else if (court.phase === "waiting") swingReason = "Waiting for an opponent.";
  else if (court.phase === "point" || court.phase === "over") swingReason = "Wait for the next serve.";
  const won = mine.tennisWins ? [`Matches won this visit: ${mine.tennisWins}`] : [];
  return {
    ...base,
    zone: "tennis",
    title: `Tennis court ${courtIndex + 1}`,
    lines: [scoreLine(court, side), ...(court.message ? [court.message] : []), ...won],
    buttons: [
      button("swing", swingLabel, "F", { type: "swing" }, swingReason, { primary: true, aimed: true }),
      button("leave", "Leave court", "R", { type: "tennis_leave" }, null),
    ],
    mySide: side,
    help: TENNIS_HELP,
    hint: `Swing when the ring is green: the ball within about 1.8 m, on your side, before its second bounce. First to ${TENNIS_POINTS_TO_WIN}, win by ${TENNIS_WIN_BY}; the serve alternates every point.`,
  };
}

/** "left", "centre" or "right" of the opponent's court as the player on `side` sees it, for a world-x aim zone. */
export function tennisAimLabel(aim: TennisAim, side: number | null): string {
  if (aim === 0) return "centre";
  const westIsLeft = side !== 1;
  return (aim === -1) === westIsLeft ? "left" : "right";
}
